import { Widget } from "@typeform/embed-react";
import { useParams, useSearchParams } from "react-router-dom";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { useContext, useEffect, useMemo, useState } from "react";
import { DssContext } from "../../shared/providers/dss/dss.provider";
import { IDSSForm, IFormValues } from "../../shared/interfaces/dss.interface";
import { DSS_SECTION, FORM_TYPE } from "../../shared/constants";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { delay, formatTemplate, getFromattedPath, markdownToHtml } from "../../shared/helpers/utils";
import Link from "../../components/Link/Link";
import { routes } from "../../shared/router/routes";
import Button from "../../components/Button/Button";
import { UserContext } from "../../shared/providers/user/user.provider";
import Modal from "../../components/Modal/Modal";

const DssForm = () => {
    const { lang, translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { user } = useContext(UserContext);
    const { forms, updateForms } = useContext(DssContext);
    const { formId } = useParams();
    const [query] = useSearchParams();

    const [form, setForm] = useState<IDSSForm>();
    const [value, setValue] = useState<number | undefined>(0);
    const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const nextForm = useMemo(() => {
        if (!form) return null;

        // FIND ALL FORMS FROM THE SAME SECTION THAT ARE NOT ASSESSED
        const sectionForms = forms.filter((f) => f.section === form.section && !f.value);

        // GET THE CURRENT FORM INDEX
        const index = sectionForms.findIndex((f) => f.formId === form?.formId);

        // RETURN THE NEXT FORM IF THERE IS ANY
        return sectionForms[index + 1] ?? undefined;
    }, [form, forms]);

    useEffect(() => {
        const form = forms.find((form) => form.formId === formId);
        if (!form) return;

        setForm(form);

        if (query.get("reassess") === "true") setValue(undefined);
        else {
            setIsSuccess(!isNaN(form.value!) ? true : undefined);
            setValue(form.value);
        }
    }, [forms, formId]);

    const handleSubmit = async (event: { formId: string; responseId: string }) => {
        if (!lang) return;

        setIsLoading(true);

        try {
            await delay(2000);
            const success = await updateForms();
            if (success) setIsSuccess(true);
            else setIsSuccess(false);
        } catch (e) {
            setIsSuccess(false);
        }

        setIsLoading(false);
    };

    const colors = useMemo(() => {
        if (form?.value === undefined)
            return { bg: "bg-neutrals-white", main: "bg-blue-50", color: "color-blue-500" };
        if (form.value > 79)
            return { bg: "bg-green-100", main: "bg-green-500", color: "color-neutrals-white" };
        if (form.value > 59)
            return { bg: "bg-yellow-100", main: "bg-yellow-500", color: "color-neutrals-white" };
        if (form.value >= 0)
            return { bg: "bg-red-100", main: "bg-red-500", color: "color-neutrals-white" };

        return { bg: "bg-neutrals-white", main: "bg-blue-50", color: "color-blue-500" };
    }, [form]);

    const feedback = useMemo(() => {
        if (form?.value === undefined) return "";

        if (form.value > 79) return form.feedback_80;
        if (form.value > 59) return form.feedback_60;
        if (form.value >= 0) return form.feedback_0;

        return "";
    }, [form]);

    const content = useMemo(() => {
        if (form?.value === undefined) return "";

        if (form.value > 79) return form.content_80;
        if (form.value > 59) return form.content_60;
        if (form.value >= 0) return form.content_0;

        return "";
    }, [form]);

    if (!formId) return <></>;
    return (
        <div className="d-flex flex-column gap-23 mb-23">
            <section aria-labelledby="title">
                <span className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                    <i className="icon icon-24 icon-neurology" aria-hidden="true" />
                    {form?.section === DSS_SECTION.MENTAL
                        ? translate("dss.mental_health", "Mental health")
                        : translate("dss.physical_health", "Physical health")}
                </span>
                <h2 id="title" className="text-h1-bd mb-2">
                    {form?.title}
                </h2>

                {isSuccess === undefined && (
                    <Widget
                        inlineOnMobile
                        id={`${formId}`}
                        hidden={{ user_id: user?.id ?? "" }}
                        className="typeform"
                        onSubmit={handleSubmit}
                        onQuestionChanged={(e) => console.log(e)}
                        onStarted={(e) => console.log(e)}
                    />
                )}

                {isSuccess && (
                    <div className="endscreen is_success px-11 py-2 p-md-4 pe-md-31 d-flex gap-22 gap-md-53 align-items-center align-items-md-start">
                        <div className="d-flex flex-column gap-1 gap-md-03">
                            <h3 className="text-h3-bd text-h1-bd-md">
                                {formatTemplate(
                                    translate(
                                        "dss.score_message",
                                        "Your workplace has a score of {0}%!"
                                    ),
                                    [`${value}`]
                                )}
                            </h3>
                            <p className="m-0 text-bd1-rg">{feedback}</p>
                            <div className="d-flex flex-wrap flex-md-nowrap gap-12 mt-12 mt-md-13">
                                <Button
                                    type="button"
                                    variant="solid"
                                    onClick={() => setIsSuccess(undefined)}>
                                    {translate("dss.reassess", "Reassess your workplace")}
                                </Button>
                                <Button type="button" variant="outlined" onClick={() => setIsOpen(true)}>
                                    {translate("common.suggestions_to_improve", "Suggestions to improve")}
                                </Button>
                                <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
                                    <div className="rich_text" dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}></div>
                                </Modal>
                            </div>
                        </div>
                        <div className="score">
                            <span
                                className={`d-flex align-items-center justify-content-center color-neutrals-white ${colors.main}`}>
                                {value}%
                            </span>
                        </div>
                    </div>
                )}

                {!isSuccess && isSuccess !== undefined && (
                    <div className="endscreen is_success px-11 py-2 p-md-4 d-flex gap-22 gap-md-53 align-items-center align-items-md-start">
                        <div className="d-flex flex-column gap-1 gap-md-03">
                            <h3 className="text-h3-bd text-h1-bd-md">Oops...</h3>
                            <p className="message m-0 text-bd1-rg">
                                {translate(
                                    "dds.error_message",
                                    "Something went wrong. We're working on resolving this, please try again later!"
                                )}
                            </p>
                        </div>
                        <div className="error">
                            <span className="d-flex align-items-center justify-content-center color-red-500">
                                <i className="icon icon-48 icon-info"></i>
                            </span>
                        </div>
                    </div>
                )}

                {!!nextForm && isSuccess !== undefined && (
                    <div className="category_next d-flex flex-column gap-03 mt-2">
                        <h3 className="text-h5-bd">
                            {translate("dss.assess_next_category", "Assess the next category")}
                        </h3>
                        <article className="d-flex justify-content-md-between align-itemsmd--center flex-column flex-md-row gap-3 gap-md-1 px-11 py-22 bg-neutrals-white">
                            <div className="d-flex gap-02 flex-column">
                                <span className="text-bd4-bd color-neutrals-dark text-uppercase">
                                    {translate("dss.no_assessment", "No assessment done yet")}
                                </span>
                                <h4 className="text-h4-md">{nextForm.title}</h4>
                            </div>
                            <Link to={getFromattedPath(routes.DSS_FORM.path, { formId: nextForm.formId })} variant="solid" customCss="px-21">
                                {translate("dss.start_assessment", "Start assessment")}
                            </Link>
                        </article>
                    </div>
                )}
            </section>
        </div>
    );
};

export default DssForm;
